/**
 * The world's seasons and its day (D288). Both are looks only: nothing that earns reads them.
 *
 * A festival year runs spring to winter as it nears the point the festival is worth holding
 * (`idleFestivalAdvice` says hold), so the festival lands after the harvest. Day and night turn
 * on a cycle from the server's clock. Both are pure functions of state and time.
 */
import type { IdleContent } from '@hedgerow/data';
import {
  idleFestivalAdvice,
  idleFestivalCoinsFor,
  idleFestivalFromGoals,
  idleFestivalNeeds,
  idleFestivalReadyInMs,
} from './festival.ts';
import type { IdleWorldState } from './state.ts';

export interface IdleSeason {
  /** The season's id from `content.seasons.list`. */
  season: string;
  /** How far the year has come, 0 to 1. 1 once the festival is worth holding or has been held. */
  progress: number;
  /** How far through this season, 0 to 1. */
  seasonProgress: number;
}

export interface IdleDayPhase {
  /** The phase's id from `content.day.phases`: dawn, day, dusk or night. */
  phase: string;
  /** How far through the whole cycle, 0 to 1. */
  progress: number;
  /** How far through this phase, 0 to 1. */
  phaseProgress: number;
}

/** Which of a rising list of `from` points `p` falls in, and how far through it. */
function within<T extends { id: string; from: number }>(
  list: readonly T[],
  p: number,
): { id: string; part: number } {
  let i = 0;
  for (let j = 0; j < list.length; j++) if (p >= list[j]!.from) i = j;
  const from = list[i]!.from;
  const to = list[i + 1]?.from ?? 1;
  return {
    id: list[i]!.id,
    part: to > from ? Math.min(1, Math.max(0, (p - from) / (to - from))) : 1,
  };
}

/**
 * How far the year has come towards the point the festival is worth holding, 0 to 1. It is the
 * year's coins earned against the coins it needs, to `content.seasons.power`, and held back by any
 * time the year must still run. 1 when the advice is to hold, or the festival is waiting (D276).
 */
export function idleYearProgress(state: IdleWorldState, content: IdleContent, now: number): number {
  if (state.festivalPending) return 1;
  const advice = idleFestivalAdvice(state, content, now);
  if (advice.advice === 'hold') return 1;
  // D322: the Heritage that makes the festival ready, less what goals add, in coins still to earn.
  const goals = idleFestivalFromGoals(state, content, advice.heritage);
  const need = Math.max(
    idleFestivalNeeds(state, content),
    idleFestivalCoinsFor(state, content, Math.max(1, advice.readyHeritage - goals)),
  );
  const earned = Math.max(0, state.year.earned);
  const coins = earned + need > 0 ? Math.pow(earned / (earned + need), content.seasons.power) : 0;
  // A year that must still run a while (D383) is held back in step with it.
  const wait = idleFestivalReadyInMs(state, content);
  const time = wait > 0 ? advice.yearMs / (advice.yearMs + wait) : 1;
  // D321: a year that runs on towards `ready.maxYearMs` nears its end however little it earns.
  const late = advice.yearMs / content.festival.ready.maxYearMs;
  const p = Math.min(Math.max(coins, late), time);
  // Short of the hold point, the year is never quite at its end.
  return Number.isFinite(p) ? Math.min(Math.max(0, p), 0.999) : 0;
}

/** The season at `now` (D288): spring to winter through the year. Looks only. */
export function idleSeason(state: IdleWorldState, content: IdleContent, now: number): IdleSeason {
  const progress = idleYearProgress(state, content, now);
  const { id, part } = within(content.seasons.list, progress);
  return { season: id, progress, seasonProgress: part };
}

/** The part of the day at `now` (D288), on a cycle of `content.day.cycleMs` from the clock. */
export function idleDayPhase(now: number, content: IdleContent): IdleDayPhase {
  const c = content.day.cycleMs;
  const progress = (((now % c) + c) % c) / c;
  const { id, part } = within(content.day.phases, progress);
  return { phase: id, progress, phaseProgress: part };
}
